<script setup>
/* eslint-disable no-unused-vars */ // Las variables de <script setup> se usan en el template
import { computed, onMounted, reactive, ref } from "vue"; // eslint-disable-line
import api from "@/servicios/apiSRP"; // eslint-disable-line
import swal from "sweetalert";

/** Variables  */
const codigos = ref([]);
const rubros = ref([]);
const gastos = ref([]);
const cargando = ref(true);
const guardando = ref(false);
const pestana = ref("codigos");

const nuevoCodigo = ref({ claveCodigo: null, rubroId: "", gastoId: "", validacionMesaAyuda: false });
const actualizandoValidacion = ref(null);
const actualizandoEstatus = ref(null);

const mostrarNuevoRubro = ref(false);
const nuevoRubro = ref({ nombreRubro: "" });

const mostrarNuevoGasto = ref(false);
const nuevoGasto = ref({ nombreGasto: "", cuentaContable: "", descripcionCuentaContable: "" });

/** Búsqueda, filtros y orden por tabla */
const buscador = reactive({ codigos: "", rubros: "", gastos: "" });
const filtroCodigos = reactive({ mesaAyuda: [], estatus: [] });
const filtroRubros = reactive({ estatus: [] });
const filtroGastos = reactive({ estatus: [] });
const orden = reactive({
  codigos: { columna: "claveCodigo", asc: true },
  rubros: { columna: "nombreRubro", asc: true },
  gastos: { columna: "nombreGasto", asc: true }
});

/** Computadas  */
const rubrosActivos = computed(() => rubros.value.filter((r) => r.activo));
const gastosActivos = computed(() => gastos.value.filter((g) => g.activo));

/** Un código está vigente si él, su rubro y su tipo de gasto están activos */
const CodigoVigente = (c) => c.activo && c.rubroActivo && c.gastoActivo;

const codigosFiltrados = computed(() => {
  const texto = buscador.codigos.trim().toLowerCase();
  const lista = codigos.value.filter((c) => {
    const coincide =
      !texto ||
      c.claveCodigo.toString().includes(texto) ||
      (c.nombreRubro || "").toLowerCase().includes(texto) ||
      (c.nombreGasto || "").toLowerCase().includes(texto) ||
      (c.cuentaContable || "").toLowerCase().includes(texto);
    const mesaAyuda =
      filtroCodigos.mesaAyuda.length == 0 || filtroCodigos.mesaAyuda.includes(c.validacionMesaAyuda ? "si" : "no");
    const estatus =
      filtroCodigos.estatus.length == 0 || filtroCodigos.estatus.includes(CodigoVigente(c) ? "activo" : "inactivo");
    return coincide && mesaAyuda && estatus;
  });
  return Ordenar(lista, orden.codigos);
});

const conteoCodigosRubro = computed(() => Contar(codigos.value, "rubroId"));
const conteoCodigosGasto = computed(() => Contar(codigos.value, "gastoId"));

const rubrosFiltrados = computed(() => {
  const texto = buscador.rubros.trim().toLowerCase();
  const lista = rubros.value.filter((r) => {
    const coincide =
      !texto || r.tipoRubroId.toString().includes(texto) || (r.nombreRubro || "").toLowerCase().includes(texto);
    const estatus = filtroRubros.estatus.length == 0 || filtroRubros.estatus.includes(r.activo ? "activo" : "inactivo");
    return coincide && estatus;
  });
  return Ordenar(lista, orden.rubros);
});

const gastosFiltrados = computed(() => {
  const texto = buscador.gastos.trim().toLowerCase();
  const lista = gastos.value.filter((g) => {
    const coincide =
      !texto ||
      g.tipoGastoId.toString().includes(texto) ||
      (g.nombreGasto || "").toLowerCase().includes(texto) ||
      (g.cuentaContable || "").toLowerCase().includes(texto) ||
      (g.descripcionCuentaContable || "").toLowerCase().includes(texto);
    const estatus = filtroGastos.estatus.length == 0 || filtroGastos.estatus.includes(g.activo ? "activo" : "inactivo");
    return coincide && estatus;
  });
  return Ordenar(lista, orden.gastos);
});

/** Combinación ya registrada para el rubro y tipo de gasto seleccionados */
const combinacionExistente = computed(() => {
  const { rubroId, gastoId } = nuevoCodigo.value;
  if (!rubroId || !gastoId) return null;
  return codigos.value.find((c) => c.rubroId == rubroId && c.gastoId == gastoId) || null;
});

const claveRepetida = computed(() => {
  const clave = Number(nuevoCodigo.value.claveCodigo);
  if (!clave) return null;
  return codigos.value.find((c) => c.claveCodigo == clave) || null;
});

const puedeGuardar = computed(
  () =>
    !guardando.value &&
    nuevoCodigo.value.rubroId &&
    nuevoCodigo.value.gastoId &&
    Number(nuevoCodigo.value.claveCodigo) > 0 &&
    Number.isInteger(Number(nuevoCodigo.value.claveCodigo)) &&
    !combinacionExistente.value &&
    !claveRepetida.value
);

/** Métodos  */
function MensajeError(error) {
  const mensaje = error && error.response && error.response.data && error.response.data.mensaje;
  return mensaje || `${error}`;
}

function Contar(lista, campo) {
  return lista.reduce((acc, item) => {
    acc[item[campo]] = (acc[item[campo]] || 0) + 1;
    return acc;
  }, {});
}

function Ordenar(lista, criterio) {
  const copia = lista.slice();
  copia.sort((a, b) => {
    let x = a[criterio.columna];
    let y = b[criterio.columna];
    if (typeof x == "string") x = x.toLowerCase();
    if (typeof y == "string") y = y.toLowerCase();
    return (x === y ? 0 : x > y ? 1 : -1) * (criterio.asc ? 1 : -1);
  });
  return copia;
}

function OrdenarPor(tabla, columna) {
  const criterio = orden[tabla];
  criterio.asc = criterio.columna === columna ? !criterio.asc : true;
  criterio.columna = columna;
}

function OrdenarClass(tabla, columna) {
  const criterio = orden[tabla];
  return criterio.columna === columna ? `sorted active ${criterio.asc ? "asc" : "desc"}` : "sorted desc";
}

async function CargarCatalogos() {
  cargando.value = true;
  try {
    const [respCodigos, respRubros, respGastos] = await Promise.all([
      api.SRP.RequisicionPago.ActualizarCatalogo.ListarCodigosPresupuesto(),
      api.SRP.RequisicionPago.ActualizarCatalogo.ListarTiposRubro(),
      api.SRP.RequisicionPago.ActualizarCatalogo.ListarTiposGasto()
    ]);
    codigos.value = Array.from(respCodigos.data);
    rubros.value = Array.from(respRubros.data);
    gastos.value = Array.from(respGastos.data);
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    cargando.value = false;
  }
}

async function GuardarRubro() {
  const nombreRubro = nuevoRubro.value.nombreRubro.trim();
  if (!nombreRubro) {
    swal("Atención", "Escribe el nombre del rubro.", "warning");
    return;
  }
  try {
    const { data } = await api.SRP.RequisicionPago.ActualizarCatalogo.GuardarTipoRubro({ nombreRubro });
    if (data.estado != "OK") {
      swal("Atención", data.mensaje, "warning");
      return;
    }
    rubros.value = [...rubros.value, data.data];
    nuevoCodigo.value.rubroId = data.data.tipoRubroId;
    nuevoRubro.value = { nombreRubro: "" };
    mostrarNuevoRubro.value = false;
    swal("¡Excelente!", data.mensaje, "success");
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  }
}

async function GuardarGasto() {
  const nombreGasto = nuevoGasto.value.nombreGasto.trim();
  if (!nombreGasto) {
    swal("Atención", "Escribe el nombre del tipo de gasto.", "warning");
    return;
  }
  try {
    const { data } = await api.SRP.RequisicionPago.ActualizarCatalogo.GuardarTipoGasto({
      nombreGasto,
      cuentaContable: nuevoGasto.value.cuentaContable.trim() || undefined,
      descripcionCuentaContable: nuevoGasto.value.descripcionCuentaContable.trim() || undefined
    });
    if (data.estado != "OK") {
      swal("Atención", data.mensaje, "warning");
      return;
    }
    gastos.value = [...gastos.value, data.data];
    nuevoCodigo.value.gastoId = data.data.tipoGastoId;
    nuevoGasto.value = { nombreGasto: "", cuentaContable: "", descripcionCuentaContable: "" };
    mostrarNuevoGasto.value = false;
    swal("¡Excelente!", data.mensaje, "success");
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  }
}

async function GuardarCodigo() {
  if (!puedeGuardar.value) return;
  guardando.value = true;
  try {
    const { data } = await api.SRP.RequisicionPago.ActualizarCatalogo.GuardarCodigoPresupuesto({
      claveCodigo: Number(nuevoCodigo.value.claveCodigo),
      rubroId: Number(nuevoCodigo.value.rubroId),
      gastoId: Number(nuevoCodigo.value.gastoId),
      validacionMesaAyuda: nuevoCodigo.value.validacionMesaAyuda
    });
    if (data.estado != "OK") {
      swal("Atención", data.mensaje, "warning");
      return;
    }
    codigos.value = [...codigos.value, data.data];
    nuevoCodigo.value = { claveCodigo: null, rubroId: "", gastoId: "", validacionMesaAyuda: false };
    swal("¡Excelente!", data.mensaje, "success");
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    guardando.value = false;
  }
}

async function CambiarValidacionMesaAyuda(codigo) {
  const valorNuevo = !codigo.validacionMesaAyuda;
  actualizandoValidacion.value = codigo.claveCodigo;
  try {
    const { data } = await api.SRP.RequisicionPago.ActualizarCatalogo.ActualizarValidacionMesaAyuda({
      claveCodigo: codigo.claveCodigo,
      validacionMesaAyuda: valorNuevo
    });
    if (data.estado != "OK") {
      swal("Atención", data.mensaje, "warning");
      return;
    }
    codigo.validacionMesaAyuda = valorNuevo;
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    actualizandoValidacion.value = null;
  }
}

/** Pide confirmación antes de desactivar; activar no requiere confirmación */
async function ConfirmarDesactivar(tipo, nombre, codigosAsociados) {
  const aviso = codigosAsociados
    ? ` Tiene ${codigosAsociados} código(s) de presupuesto asociados que dejarán de estar disponibles en las requisiciones.`
    : "";
  return await swal({
    title: `¿Desactivar ${tipo}?`,
    text: `"${nombre}" ya no se podrá seleccionar en nuevas requisiciones.${aviso}`,
    icon: "warning",
    buttons: ["Cancelar", "Desactivar"],
    dangerMode: true
  });
}

async function CambiarEstatusRubro(rubro) {
  const activo = !rubro.activo;
  if (!activo && !(await ConfirmarDesactivar("el rubro", rubro.nombreRubro, conteoCodigosRubro.value[rubro.tipoRubroId])))
    return;
  actualizandoEstatus.value = `rubro-${rubro.tipoRubroId}`;
  try {
    const { data } = await api.SRP.RequisicionPago.ActualizarCatalogo.ActualizarEstatusTipoRubro({
      id: rubro.tipoRubroId,
      activo
    });
    if (data.estado != "OK") {
      swal("Atención", data.mensaje, "warning");
      return;
    }
    rubro.activo = activo;
    codigos.value.filter((c) => c.rubroId == rubro.tipoRubroId).forEach((c) => (c.rubroActivo = activo));
    if (!activo && nuevoCodigo.value.rubroId == rubro.tipoRubroId) nuevoCodigo.value.rubroId = "";
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    actualizandoEstatus.value = null;
  }
}

async function CambiarEstatusGasto(gasto) {
  const activo = !gasto.activo;
  if (
    !activo &&
    !(await ConfirmarDesactivar("el tipo de gasto", gasto.nombreGasto, conteoCodigosGasto.value[gasto.tipoGastoId]))
  )
    return;
  actualizandoEstatus.value = `gasto-${gasto.tipoGastoId}`;
  try {
    const { data } = await api.SRP.RequisicionPago.ActualizarCatalogo.ActualizarEstatusTipoGasto({
      id: gasto.tipoGastoId,
      activo
    });
    if (data.estado != "OK") {
      swal("Atención", data.mensaje, "warning");
      return;
    }
    gasto.activo = activo;
    codigos.value.filter((c) => c.gastoId == gasto.tipoGastoId).forEach((c) => (c.gastoActivo = activo));
    if (!activo && nuevoCodigo.value.gastoId == gasto.tipoGastoId) nuevoCodigo.value.gastoId = "";
  } catch (error) {
    swal("¡Ups!", MensajeError(error), "error");
  } finally {
    actualizandoEstatus.value = null;
  }
}

function MotivoInactivo(c) {
  const motivos = [];
  if (!c.activo) motivos.push("Código inactivo");
  if (!c.rubroActivo) motivos.push("Rubro inactivo");
  if (!c.gastoActivo) motivos.push("Tipo de gasto inactivo");
  return motivos.join(", ");
}

/** Ciclos de vida  */
onMounted(async () => {
  await CargarCatalogos();
});
</script>
<template>
  <div class="container-fluid">
    <h2 class="text-secondary mb-3">Catálogo de códigos de presupuesto</h2>

    <!-- Alta de combinación -->
    <section class="card mb-4">
      <div class="card-header bg-white">
        <h6 class="text-secondary mb-0">Nueva combinación rubro - tipo de gasto</h6>
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label for="selectRubro" class="form-label">Rubro</label>
            <div class="input-group">
              <select id="selectRubro" class="form-select" v-model="nuevoCodigo.rubroId" :disabled="cargando">
                <option value="" disabled>Selecciona un rubro</option>
                <option v-for="rubro in rubrosActivos" :key="rubro.tipoRubroId" :value="rubro.tipoRubroId">
                  {{ rubro.nombreRubro }}
                </option>
              </select>
              <button
                class="btn btn-outline-secondary"
                type="button"
                title="Agregar rubro"
                @click="mostrarNuevoRubro = !mostrarNuevoRubro"
              >
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
          <div class="col-md-4">
            <label for="selectGasto" class="form-label">Tipo de gasto</label>
            <div class="input-group">
              <select id="selectGasto" class="form-select" v-model="nuevoCodigo.gastoId" :disabled="cargando">
                <option value="" disabled>Selecciona un tipo de gasto</option>
                <option v-for="gasto in gastosActivos" :key="gasto.tipoGastoId" :value="gasto.tipoGastoId">
                  {{ gasto.nombreGasto }}{{ gasto.cuentaContable ? ` (${gasto.cuentaContable})` : "" }}
                </option>
              </select>
              <button
                class="btn btn-outline-secondary"
                type="button"
                title="Agregar tipo de gasto"
                @click="mostrarNuevoGasto = !mostrarNuevoGasto"
              >
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
          <div class="col-md-2">
            <label for="inputClaveCodigo" class="form-label">Código</label>
            <input
              id="inputClaveCodigo"
              type="number"
              min="1"
              step="1"
              class="form-control"
              placeholder="Ej. 1001"
              v-model="nuevoCodigo.claveCodigo"
            />
          </div>
          <div class="col-md-2 d-grid">
            <button class="btn btn-primary text-white" type="button" :disabled="!puedeGuardar" @click="GuardarCodigo()">
              <i class="fas fa-save"></i> {{ guardando ? "Guardando..." : "Guardar" }}
            </button>
          </div>
        </div>

        <div class="form-check mt-3">
          <input
            class="form-check-input"
            type="checkbox"
            id="checkValidacionMesaAyuda"
            v-model="nuevoCodigo.validacionMesaAyuda"
          />
          <label class="form-check-label" for="checkValidacionMesaAyuda">Validación de Mesa de ayuda</label>
        </div>

        <p class="text-danger small mt-2 mb-0" v-if="combinacionExistente">
          Esta combinación ya tiene asignado el código {{ combinacionExistente.claveCodigo }}.
        </p>
        <p class="text-danger small mt-2 mb-0" v-if="claveRepetida">
          El código {{ claveRepetida.claveCodigo }} ya está asignado a "{{ claveRepetida.nombreRubro }}" - "{{
            claveRepetida.nombreGasto
          }}".
        </p>

        <!-- Alta de rubro -->
        <div class="subformulario mt-3" v-if="mostrarNuevoRubro">
          <h6 class="text-secondary">Nuevo rubro</h6>
          <div class="row g-2 align-items-end">
            <div class="col-md-8">
              <input
                type="text"
                class="form-control"
                maxlength="250"
                placeholder="Nombre del rubro"
                v-model="nuevoRubro.nombreRubro"
                @keyup.enter="GuardarRubro()"
              />
            </div>
            <div class="col-md-4 d-flex gap-2">
              <button class="btn btn-primary text-white" type="button" @click="GuardarRubro()">Agregar</button>
              <button class="btn btn-outline-secondary" type="button" @click="mostrarNuevoRubro = false">
                Cancelar
              </button>
            </div>
          </div>
        </div>

        <!-- Alta de tipo de gasto -->
        <div class="subformulario mt-3" v-if="mostrarNuevoGasto">
          <h6 class="text-secondary">Nuevo tipo de gasto</h6>
          <div class="row g-2 align-items-end">
            <div class="col-md-4">
              <input
                type="text"
                class="form-control"
                maxlength="100"
                placeholder="Nombre del tipo de gasto"
                v-model="nuevoGasto.nombreGasto"
              />
            </div>
            <div class="col-md-2">
              <input
                type="text"
                class="form-control"
                maxlength="13"
                placeholder="Cuenta contable"
                v-model="nuevoGasto.cuentaContable"
              />
            </div>
            <div class="col-md-3">
              <input
                type="text"
                class="form-control"
                maxlength="50"
                placeholder="Descripción cuenta contable"
                v-model="nuevoGasto.descripcionCuentaContable"
              />
            </div>
            <div class="col-md-3 d-flex gap-2">
              <button class="btn btn-primary text-white" type="button" @click="GuardarGasto()">Agregar</button>
              <button class="btn btn-outline-secondary" type="button" @click="mostrarNuevoGasto = false">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pestañas de consulta -->
    <ul class="nav nav-pills mb-3">
      <li class="nav-item">
        <a class="nav-link" :class="{ active: pestana == 'codigos' }" href="#" @click.prevent="pestana = 'codigos'">
          Códigos de presupuesto <span class="badge bg-light text-secondary">{{ codigos.length }}</span>
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: pestana == 'rubros' }" href="#" @click.prevent="pestana = 'rubros'">
          Rubros <span class="badge bg-light text-secondary">{{ rubros.length }}</span>
        </a>
      </li>
      <li class="nav-item">
        <a class="nav-link" :class="{ active: pestana == 'gastos' }" href="#" @click.prevent="pestana = 'gastos'">
          Tipos de gasto <span class="badge bg-light text-secondary">{{ gastos.length }}</span>
        </a>
      </li>
    </ul>

    <!-- Tabla: Códigos de presupuesto -->
    <div class="card" v-show="pestana == 'codigos'">
      <div class="card-header bg-white">
        <div class="row">
          <div class="col-5">
            <div class="form-group">
              <div class="row">
                <div class="col-1">
                  <a class="btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="fas fa-filter"></i>
                  </a>
                  <ul class="dropdown-menu">
                    <label class="list-group-item border-0"><strong> Filtro por: Mesa de ayuda </strong></label>
                    <li class="list-group-item border-0">
                      <input class="form-check-input me-1" type="checkbox" value="si" v-model="filtroCodigos.mesaAyuda" />
                      <label>Con validación</label>
                    </li>
                    <li class="list-group-item border-0">
                      <input class="form-check-input me-1" type="checkbox" value="no" v-model="filtroCodigos.mesaAyuda" />
                      <label>Sin validación</label>
                    </li>
                    <label class="list-group-item border-0"><strong> Filtro por: Estatus </strong></label>
                    <li class="list-group-item border-0">
                      <input class="form-check-input me-1" type="checkbox" value="activo" v-model="filtroCodigos.estatus" />
                      <label>Activo</label>
                    </li>
                    <li class="list-group-item border-0">
                      <input
                        class="form-check-input me-1"
                        type="checkbox"
                        value="inactivo"
                        v-model="filtroCodigos.estatus"
                      />
                      <label>Inactivo</label>
                    </li>
                  </ul>
                </div>
                <div class="col-10">
                  <span class="fas fa-search form-control-icon"></span>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Busca por código, rubro, tipo de gasto o cuenta contable"
                    v-model="buscador.codigos"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="table-responsive tabla-scroll">
          <table class="table table-hover">
            <thead>
              <tr class="text-center">
                <th scope="col">
                  <span @click="OrdenarPor('codigos', 'claveCodigo')" :class="OrdenarClass('codigos', 'claveCodigo')">
                    Código
                  </span>
                </th>
                <th scope="col">
                  <span @click="OrdenarPor('codigos', 'nombreRubro')" :class="OrdenarClass('codigos', 'nombreRubro')">
                    Rubro
                  </span>
                </th>
                <th scope="col">
                  <span @click="OrdenarPor('codigos', 'nombreGasto')" :class="OrdenarClass('codigos', 'nombreGasto')">
                    Tipo de gasto
                  </span>
                </th>
                <th scope="col">
                  <span
                    @click="OrdenarPor('codigos', 'cuentaContable')"
                    :class="OrdenarClass('codigos', 'cuentaContable')"
                  >
                    Cuenta contable
                  </span>
                </th>
                <th scope="col">
                  <span
                    @click="OrdenarPor('codigos', 'validacionMesaAyuda')"
                    :class="OrdenarClass('codigos', 'validacionMesaAyuda')"
                  >
                    Validación de Mesa de ayuda
                  </span>
                </th>
                <th scope="col"><span class="no-pointer">Estatus</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="cargando">
                <td colspan="6" class="text-center text-secondary">Cargando...</td>
              </tr>
              <tr v-else-if="codigosFiltrados.length == 0">
                <td colspan="6" class="text-center text-secondary">No se encontraron códigos.</td>
              </tr>
              <tr v-for="codigo in codigosFiltrados" :key="codigo.claveCodigo" class="text-center">
                <td class="fw-bold">{{ codigo.claveCodigo }}</td>
                <td>{{ codigo.nombreRubro }}</td>
                <td>{{ codigo.nombreGasto }}</td>
                <td>{{ codigo.cuentaContable }}</td>
                <td>
                  <div class="form-check form-switch d-inline-block">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                      :checked="codigo.validacionMesaAyuda"
                      :disabled="actualizandoValidacion == codigo.claveCodigo"
                      @click.prevent="CambiarValidacionMesaAyuda(codigo)"
                    />
                  </div>
                </td>
                <td>
                  <span
                    v-if="CodigoVigente(codigo)"
                    class="fas fa-check-circle fa-lg"
                    style="color: #26bb10"
                    title="Activo"
                  ></span>
                  <span
                    v-else
                    class="fas fa-times-circle fa-lg"
                    style="color: #fe541f"
                    :title="MotivoInactivo(codigo)"
                  ></span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer bg-white text-secondary text-end px-3">
        Mostrando {{ codigosFiltrados.length }} de {{ codigos.length }} códigos
      </div>
    </div>

    <!-- Tabla: Rubros -->
    <div class="card" v-show="pestana == 'rubros'">
      <div class="card-header bg-white">
        <div class="row">
          <div class="col-5">
            <div class="form-group">
              <div class="row">
                <div class="col-1">
                  <a class="btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="fas fa-filter"></i>
                  </a>
                  <ul class="dropdown-menu">
                    <label class="list-group-item border-0"><strong> Filtro por: Estatus </strong></label>
                    <li class="list-group-item border-0">
                      <input class="form-check-input me-1" type="checkbox" value="activo" v-model="filtroRubros.estatus" />
                      <label>Activo</label>
                    </li>
                    <li class="list-group-item border-0">
                      <input
                        class="form-check-input me-1"
                        type="checkbox"
                        value="inactivo"
                        v-model="filtroRubros.estatus"
                      />
                      <label>Inactivo</label>
                    </li>
                  </ul>
                </div>
                <div class="col-10">
                  <span class="fas fa-search form-control-icon"></span>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Busca por ID o nombre del rubro"
                    v-model="buscador.rubros"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="table-responsive tabla-scroll">
          <table class="table table-hover">
            <thead>
              <tr class="text-center">
                <th scope="col" style="width: 8%">
                  <span @click="OrdenarPor('rubros', 'tipoRubroId')" :class="OrdenarClass('rubros', 'tipoRubroId')">
                    ID
                  </span>
                </th>
                <th scope="col">
                  <span @click="OrdenarPor('rubros', 'nombreRubro')" :class="OrdenarClass('rubros', 'nombreRubro')">
                    Rubro
                  </span>
                </th>
                <th scope="col"><span class="no-pointer">Códigos asociados</span></th>
                <th scope="col">
                  <span @click="OrdenarPor('rubros', 'activo')" :class="OrdenarClass('rubros', 'activo')">
                    Activo
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="cargando">
                <td colspan="4" class="text-center text-secondary">Cargando...</td>
              </tr>
              <tr v-else-if="rubrosFiltrados.length == 0">
                <td colspan="4" class="text-center text-secondary">No se encontraron rubros.</td>
              </tr>
              <tr
                v-for="rubro in rubrosFiltrados"
                :key="rubro.tipoRubroId"
                class="text-center"
                :class="{ 'fila-inactiva': !rubro.activo }"
              >
                <td>{{ rubro.tipoRubroId }}</td>
                <td class="text-start">{{ rubro.nombreRubro }}</td>
                <td>{{ conteoCodigosRubro[rubro.tipoRubroId] || 0 }}</td>
                <td>
                  <div class="form-check form-switch d-inline-block">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                      :checked="rubro.activo"
                      :disabled="actualizandoEstatus == `rubro-${rubro.tipoRubroId}`"
                      @click.prevent="CambiarEstatusRubro(rubro)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer bg-white text-secondary text-end px-3">
        Mostrando {{ rubrosFiltrados.length }} de {{ rubros.length }} rubros
      </div>
    </div>

    <!-- Tabla: Tipos de gasto -->
    <div class="card" v-show="pestana == 'gastos'">
      <div class="card-header bg-white">
        <div class="row">
          <div class="col-5">
            <div class="form-group">
              <div class="row">
                <div class="col-1">
                  <a class="btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="fas fa-filter"></i>
                  </a>
                  <ul class="dropdown-menu">
                    <label class="list-group-item border-0"><strong> Filtro por: Estatus </strong></label>
                    <li class="list-group-item border-0">
                      <input class="form-check-input me-1" type="checkbox" value="activo" v-model="filtroGastos.estatus" />
                      <label>Activo</label>
                    </li>
                    <li class="list-group-item border-0">
                      <input
                        class="form-check-input me-1"
                        type="checkbox"
                        value="inactivo"
                        v-model="filtroGastos.estatus"
                      />
                      <label>Inactivo</label>
                    </li>
                  </ul>
                </div>
                <div class="col-10">
                  <span class="fas fa-search form-control-icon"></span>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Busca por ID, nombre o cuenta contable"
                    v-model="buscador.gastos"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div class="table-responsive tabla-scroll">
          <table class="table table-hover">
            <thead>
              <tr class="text-center">
                <th scope="col" style="width: 8%">
                  <span @click="OrdenarPor('gastos', 'tipoGastoId')" :class="OrdenarClass('gastos', 'tipoGastoId')">
                    ID
                  </span>
                </th>
                <th scope="col">
                  <span @click="OrdenarPor('gastos', 'nombreGasto')" :class="OrdenarClass('gastos', 'nombreGasto')">
                    Tipo de gasto
                  </span>
                </th>
                <th scope="col">
                  <span
                    @click="OrdenarPor('gastos', 'cuentaContable')"
                    :class="OrdenarClass('gastos', 'cuentaContable')"
                  >
                    Cuenta contable
                  </span>
                </th>
                <th scope="col"><span class="no-pointer">Descripción cuenta contable</span></th>
                <th scope="col"><span class="no-pointer">Códigos asociados</span></th>
                <th scope="col">
                  <span @click="OrdenarPor('gastos', 'activo')" :class="OrdenarClass('gastos', 'activo')">
                    Activo
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="cargando">
                <td colspan="6" class="text-center text-secondary">Cargando...</td>
              </tr>
              <tr v-else-if="gastosFiltrados.length == 0">
                <td colspan="6" class="text-center text-secondary">No se encontraron tipos de gasto.</td>
              </tr>
              <tr
                v-for="gasto in gastosFiltrados"
                :key="gasto.tipoGastoId"
                class="text-center"
                :class="{ 'fila-inactiva': !gasto.activo }"
              >
                <td>{{ gasto.tipoGastoId }}</td>
                <td class="text-start">{{ gasto.nombreGasto }}</td>
                <td>{{ gasto.cuentaContable }}</td>
                <td>{{ gasto.descripcionCuentaContable }}</td>
                <td>{{ conteoCodigosGasto[gasto.tipoGastoId] || 0 }}</td>
                <td>
                  <div class="form-check form-switch d-inline-block">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      role="switch"
                      :checked="gasto.activo"
                      :disabled="actualizandoEstatus == `gasto-${gasto.tipoGastoId}`"
                      @click.prevent="CambiarEstatusGasto(gasto)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="card-footer bg-white text-secondary text-end px-3">
        Mostrando {{ gastosFiltrados.length }} de {{ gastos.length }} tipos de gasto
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@import "src/sass/bootstrap-variables.scss";

.container-fluid {
  margin-top: 15px;
}

.card {
  font-size: 12px;
}

.subformulario {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
}

/* Encabezados ordenables, igual que en los históricos de requisiciones */
.sorted.asc::after {
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  content: "\f885";
  margin-left: 3px;
}

.sorted.desc::after {
  font-family: "Font Awesome 5 Free";
  font-weight: 900;
  content: "\f160";
  margin-left: 3px;
}

.sorted.asc.active,
.sorted.desc.active {
  color: $primary !important;
}

.sorted.asc.active::after,
.sorted.desc.active::after {
  color: $primary;
}

th {
  vertical-align: middle;
}

th span.no-pointer {
  cursor: default !important;
}

th span {
  color: $secondary;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  user-select: none;
}

td {
  vertical-align: middle;
}

.dropdown-toggle {
  &::after {
    display: none !important;
  }
}

.form-group {
  .form-control {
    padding-left: 2.375rem;
    background-color: #f2f2f2;
    color: #828282;
    border: 0px;
  }

  .form-control-icon {
    position: absolute;
    z-index: 2;
    display: block;
    width: 2rem;
    height: 2rem;
    line-height: 2rem;
    text-align: center;
    pointer-events: none;
    color: #aaa;
  }
}

.tabla-scroll {
  max-height: 520px;
  overflow-y: auto;

  thead th {
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 1;
  }
}

.fila-inactiva td {
  color: #aaa;
}

.nav-pills .nav-link {
  color: $secondary;
  cursor: pointer;

  &.active {
    background-color: $primary;
    color: white;
  }
}
</style>
